import React, { useState, useEffect } from "react";
import { Loader2, FileText, Gavel, AlertOctagon, Copy, Download, ScrollText, ShieldAlert, Scale, Target, Share2, Sparkles, FileDown } from "lucide-react";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import ShareDialog from "@/components/ShareDialog";
import { useActiveProject } from "@/lib/ActiveProjectContext";
import { downloadAsPDF } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const PROJECT_TYPES = [
  "Solar Power Plant",
  "Oil & Gas Refinery",
  "Metro / Rail",
  "Highway",
  "LNG Terminal",
  "Wind Farm",
  "Combined Cycle Power",
  "Industrial Plant",
];
const FIDIC = ["Red Book", "Yellow Book", "Silver Book", "Green Book"];

const SAMPLE_QUERY =
  "Contractor has experienced 35 days delay because the Engineer issued the IFC drawings for the inverter foundations 8 weeks late. The Employer is now claiming Liquidated Damages. Advise on entitlement, time-bar exposure and the immediate steps to protect our position.";

export default function ContractAI() {
  const { project } = useActiveProject();
  const [projectType, setProjectType] = useState(PROJECT_TYPES[0]);
  const [contractType, setContractType] = useState("Yellow Book");
  const [query, setQuery] = useState(SAMPLE_QUERY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [docOpen, setDocOpen] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const [docBody, setDocBody] = useState("");
  const [docLoading, setDocLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Prefill from active project — extract FIDIC book (strip "FIDIC " prefix) and
  // prefill query with project context if it's a custom / freshly-created one.
  useEffect(() => {
    if (!project) return;
    const book = (project.contract_type || "").replace(/^FIDIC\s+/i, "").trim();
    if (["Red Book", "Yellow Book", "Silver Book", "Green Book"].includes(book)) {
      setContractType(book);
    }
    if (project.custom) {
      const ctx = [
        `Project: ${project.name}`,
        project.client ? `Client: ${project.client}` : "",
        project.description ? `Scope: ${project.description}` : "",
        project.notes ? `Current situation: ${project.notes}` : "",
      ].filter(Boolean).join("\n");
      if (ctx) setQuery(ctx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const analyse = async () => {
    if (!query.trim()) return toast.error("Please enter the contractual issue");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/contract/analyze", {
        project_type: projectType,
        contract_type: contractType,
        query,
      });
      setResult(data);
      toast.success("Contract analysis complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const generateDoc = async (kind) => {
    setDocOpen(true);
    setDocLoading(true);
    setDocBody("");
    setDocTitle(kind === "notice" ? "Contractual Notice" : "Claim Draft");
    try {
      const url =
        kind === "notice"
          ? "/contract/generate-notice"
          : "/contract/generate-claim";
      const { data } = await api.post(url, {
        project_name: `${projectType} Project`,
        contract_type: contractType,
        clause_reference: result?.clause?.reference || null,
        event_description: query,
        contractor_position:
          result?.entitlement?.time
            ? `Seeking ${result.entitlement.time}, ${result.entitlement.cost}`
            : "EOT and additional cost",
      });
      setDocBody(data.document);
    } catch (e) {
      toast.error("Generation failed");
      setDocOpen(false);
    } finally {
      setDocLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="contract-ai-page">
      <SectionHeader
        overline="Built on FIDIC-based Contract Intelligence Framework"
        title="Contract AI"
        description="Clause interpretation, risk analysis, entitlement reasoning and time-bar awareness — engineered for EPC professionals."
        testid="contract-ai-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Input panel */}
        <div className="lg:col-span-4 bg-slate-900/60 border border-slate-800 rounded-sm p-4 space-y-4 h-fit lg:sticky lg:top-24">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">
            INPUT
          </div>
          <div>
            <Label className="text-xs text-slate-400">Project Type</Label>
            <Select value={projectType} onValueChange={setProjectType}>
              <SelectTrigger
                className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
                data-testid="select-project-type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                {PROJECT_TYPES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">FIDIC Contract</Label>
            <Select value={contractType} onValueChange={setContractType}>
              <SelectTrigger
                className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
                data-testid="select-contract-type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                {FIDIC.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Issue / Query</Label>
            <Textarea
              data-testid="contract-query"
              rows={9}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-sm text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500"
              placeholder="Describe the delay event, claim, or contractual question…"
            />
          </div>
          <Button
            onClick={analyse}
            disabled={loading}
            data-testid="analyse-btn"
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Analysing FIDIC clauses…
              </>
            ) : (
              "Run Contract Analysis"
            )}
          </Button>
          <p className="text-[10px] text-slate-600 font-mono leading-relaxed">
            Powered by Claude Sonnet 4.5 · trained on FIDIC, NEC, ICC & arbitration
            doctrines.
          </p>
        </div>

        {/* Output */}
        <div className="lg:col-span-8 space-y-4">
          {!result && !loading && <EmptyOutput />}
          {loading && <LoadingOutput />}
          {result && (
            <ResultPanel
              result={result}
              onNotice={() => generateDoc("notice")}
              onClaim={() => generateDoc("claim")}
              onShare={() => setShareOpen(true)}
            />
          )}
        </div>
      </div>

      <DocDialog
        open={docOpen}
        onOpenChange={setDocOpen}
        title={docTitle}
        body={docBody}
        loading={docLoading}
        onShare={() => setShareOpen(true)}
      />

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        kind={docBody ? `contract_${docTitle.toLowerCase().replace(/\s+/g, "_")}` : "contract_analysis"}
        title={docBody ? `${docTitle} — ${projectType}` : `Contract Analysis — ${projectType}`}
        body={docBody || null}
        data={docBody ? null : result}
        projectName={projectType}
      />
    </div>
  );
}

function EmptyOutput() {
  return (
    <div
      className="bg-slate-900/40 border border-dashed border-slate-800 rounded-sm p-12 text-center"
      data-testid="output-empty"
    >
      <Gavel className="h-10 w-10 text-slate-700 mx-auto mb-4" />
      <h3 className="font-display text-lg text-slate-300">
        Ready for FIDIC Analysis
      </h3>
      <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
        Describe the contractual event on the left. Output will be returned in
        four decision-ready tabs: Clause · Risk · Entitlement · Action.
      </p>
    </div>
  );
}

function LoadingOutput() {
  return (
    <div
      className="bg-slate-900/60 border border-slate-800 rounded-sm p-10 text-center"
      data-testid="output-loading"
    >
      <Loader2 className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-3" />
      <div className="font-mono text-xs text-cyan-400 uppercase tracking-[0.2em]">
        Reasoning…
      </div>
      <div className="text-sm text-slate-400 mt-1">
        Cross-referencing FIDIC sub-clauses, time-bar windows and entitlement
        doctrine.
      </div>
    </div>
  );
}

function ResultPanel({ result, onNotice, onClaim, onShare }) {
  const sevColor = {
    Low: "bg-emerald-500/15 text-emerald-300 border-emerald-700",
    Medium: "bg-amber-500/15 text-amber-300 border-amber-700",
    High: "bg-rose-500/15 text-rose-300 border-rose-700",
    Critical: "bg-rose-600/25 text-rose-200 border-rose-500",
  }[result.risk?.severity] || "bg-slate-800 text-slate-300";
  const conf = Number(result.ai_confidence ?? 0);
  const confColor =
    conf >= 80
      ? "text-emerald-400"
      : conf >= 60
      ? "text-cyan-400"
      : conf >= 40
      ? "text-amber-400"
      : "text-rose-400";
  return (
    <div className="space-y-4" data-testid="output-result">
      {/* Summary banner */}
      <div className="bg-slate-900/80 border-l-2 border-cyan-500 border-y border-r border-slate-800 p-4 rounded-sm">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <ShieldAlert className="h-4 w-4 text-cyan-400" />
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400">
            Executive Summary
          </span>
          {result.ai_confidence != null && (
            <div
              className="ml-auto flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500"
              data-testid="ai-confidence"
            >
              <Sparkles className={`h-3 w-3 ${confColor}`} />
              AI Confidence
              <span className={`text-sm font-bold ${confColor}`}>{conf}%</span>
            </div>
          )}
          <Badge
            className={`rounded-sm border ${sevColor}`}
            data-testid="severity-badge"
          >
            {result.risk?.severity || "—"}
          </Badge>
        </div>
        <p className="text-sm text-slate-200 leading-relaxed">
          {result.summary}
        </p>
      </div>

      <Tabs defaultValue="clause" className="w-full">
        <TabsList className="bg-transparent border-b border-slate-800 rounded-none p-0 h-auto w-full justify-start gap-1">
          {[
            { v: "clause", l: "Clause Interpretation", icon: ScrollText },
            { v: "risk", l: "Risk Analysis", icon: ShieldAlert },
            { v: "entitlement", l: "Entitlement", icon: Scale },
            { v: "action", l: "Suggested Action", icon: Target },
          ].map((t) => (
            <TabsTrigger
              key={t.v}
              value={t.v}
              data-testid={`tab-${t.v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-cyan-500 data-[state=active]:text-cyan-400 data-[state=active]:bg-transparent text-slate-400 hover:text-white px-4 py-2.5 text-sm flex items-center gap-2"
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="clause" className="mt-4">
          <Panel>
            <Field
              label="Reference"
              value={`${result.clause?.book} · ${result.clause?.reference}`}
              mono
            />
            <Field label="Interpretation" value={result.clause?.interpretation} />
          </Panel>
        </TabsContent>

        <TabsContent value="risk" className="mt-4">
          <Panel>
            <div className="grid md:grid-cols-2 gap-4">
              <RiskList
                title="Employer-side risks"
                items={result.risk?.employer_risks || []}
                color="text-cyan-300"
              />
              <RiskList
                title="Contractor-side risks"
                items={result.risk?.contractor_risks || []}
                color="text-amber-300"
              />
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="entitlement" className="mt-4">
          <Panel>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Time" value={result.entitlement?.time} />
              <Field label="Cost" value={result.entitlement?.cost} />
              <Field
                label="EOT estimate"
                value={`${result.entitlement?.eot_days_estimate ?? 0} days`}
                mono
              />
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="action" className="mt-4">
          <Panel>
            <div className="flex items-center gap-3 mb-3">
              <AlertOctagon className="h-4 w-4 text-rose-400" />
              <span className="text-xs text-slate-300">
                Urgency:{" "}
                <span className="font-mono text-rose-300">
                  {result.action?.urgency}
                </span>
              </span>
              <span className="text-xs text-slate-300 ml-4">
                Time-bar:{" "}
                <span className="font-mono text-amber-300">
                  {result.action?.time_bar_days} days
                </span>
              </span>
            </div>
            <ol className="space-y-2 list-decimal list-inside text-sm text-slate-200">
              {(result.action?.recommended_steps || []).map((s, i) => (
                <li key={i} className="leading-relaxed">
                  {s}
                </li>
              ))}
            </ol>
          </Panel>
        </TabsContent>
      </Tabs>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Button
          onClick={onNotice}
          data-testid="generate-notice-btn"
          className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
        >
          <FileText className="h-4 w-4 mr-2" /> Generate Notice
        </Button>
        <Button
          onClick={onClaim}
          variant="outline"
          data-testid="generate-claim-btn"
          className="bg-transparent border-slate-700 text-white hover:bg-slate-900 hover:text-cyan-300 rounded-sm"
        >
          <Gavel className="h-4 w-4 mr-2" /> Generate Claim Draft
        </Button>
        <Button
          onClick={onShare}
          variant="outline"
          data-testid="share-stakeholders-btn"
          className="bg-transparent border-slate-700 text-white hover:bg-slate-900 hover:text-cyan-300 rounded-sm sm:ml-auto"
        >
          <Share2 className="h-4 w-4 mr-2" /> Share with Stakeholders
        </Button>
      </div>
    </div>
  );
}

function Panel({ children }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-4 space-y-3">
      {children}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">
        {label}
      </div>
      <div
        className={`text-sm text-slate-100 leading-relaxed ${
          mono ? "font-mono" : ""
        }`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function RiskList({ title, items, color }) {
  return (
    <div>
      <div className={`text-xs font-semibold mb-2 ${color}`}>{title}</div>
      <ul className="space-y-1.5">
        {items.length === 0 && (
          <li className="text-xs text-slate-500">— None identified</li>
        )}
        {items.map((it, i) => (
          <li key={i} className="text-sm text-slate-200 flex gap-2">
            <span className="text-slate-600 font-mono text-xs mt-1">▸</span>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DocDialog({ open, onOpenChange, title, body, loading, onShare }) {
  const copy = () => {
    navigator.clipboard.writeText(body);
    toast.success("Copied to clipboard");
  };
  const download = () => {
    const blob = new Blob([body], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "-").toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const downloadPdf = () => {
    downloadAsPDF({ title, body, kind: title });
    toast.success("PDF downloaded");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-slate-800 text-slate-100 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col rounded-sm"
        data-testid="doc-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText className="h-4 w-4 text-cyan-400" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Ready for official contractual issuance · review before issue
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto scroll-clean bg-slate-900/40 border border-slate-800 rounded-sm p-5">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-6 w-6 text-cyan-400 animate-spin mx-auto mb-3" />
              <div className="text-xs font-mono text-slate-400 uppercase tracking-[0.2em]">
                Drafting document…
              </div>
            </div>
          ) : (
            <pre
              className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-slate-200"
              data-testid="doc-body"
            >
              {body}
            </pre>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          {onShare && (
            <Button
              variant="outline"
              size="sm"
              onClick={onShare}
              disabled={!body}
              data-testid="doc-share-btn"
              className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm mr-auto"
            >
              <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={copy}
            disabled={!body}
            data-testid="doc-copy-btn"
            className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={download}
            disabled={!body}
            data-testid="doc-download-md-btn"
            className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> .md
          </Button>
          <Button
            size="sm"
            onClick={downloadPdf}
            disabled={!body}
            data-testid="doc-download-btn"
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 rounded-sm font-semibold"
          >
            <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
